import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAccessKey } from "./baseQuery";

export const productApi = createApi({
  reducerPath: "productApi",
  baseQuery: baseQueryWithAccessKey,
  // so, here, we can specify multiple tags, and then inside the endpoint, we can specify which tag is associated with
  // which query using providesTags, and inside post endpoints, we can then invalidate the query related to certain tag
  // so e-g., I give "all-products" to the getAllProducts endpoint, then inside updateCost, I can go ahead and invalidate
  // the query against "all-products" tag. This is basically the same as query keys in tanstack/react-query
  tagTypes: [
    "all-products",
    "product-details",
    "product-variant-details",
    "product-variant-cost-details",
    "all-product-ad-stats",
  ],
  endpoints: (builder) => ({
    getProducts: builder.query({
      query: ({ url, params }) => ({
        // this query function "constructs" the api URL to be called
        // it can either return a string or an object. If it returns a string, that's the URL value
        // e-g., return "/api/posts", otherwise, if it returns an object, we can define named properties
        // as can be seen in this example. We receive the "options" object from where the hook is being called
        // destructure the object, get the URL and params, and pass it in the return object
        url,
        params,
      }),
      // it can either be this simple syntax
      // providesTags: ["all-products"] or
      providesTags: [
        { type: "all-products" /*id: we can give it an id as well*/ },
      ],
    }),
    getProductDetails: builder.query({
      query: (params) => ({
        // this example makes the above comment more clear
        url: "/api/glabp/",
        params,
      }),
      // the single product's details will be cached against the ID, so we can invalidate only what's required
      providesTags: (result, error, args) => ["product-details", args?.glid],
    }),
    getProductVariantDetails: builder.query({
      query: (params) => ({
        url: "/api/glabp/",
        params,
      }),
      providesTags: ["product-variant-details"],
    }),
    getVariantCostDetails: builder.query({
      query: (params) => ({
        url: "/api/glabp/",
        params,
      }),
      providesTags: (result, error, args) => [
        { type: "product-variant-cost-details", id: args?.glid },
      ],
    }),
    getProductAdStats: builder.query({
      query: ({ glid, chart_type, start_date, end_date }) => ({
        url: "/api/glabp/",
        params: { page: "ChartData", glid, chart_type, start_date, end_date },
      }),
      providesTags: ["all-product-ad-stats"],
    }),
    updateCostAndSupply: builder.mutation({
      query: (payload) => ({
        url: "/api/glabp/",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, args) => [
        { type: "product-details", id: args?.glid },
      ],
    }),
    updateAllCostsForProduct: builder.mutation({
      query: (payload) => ({
        url: "/api/glabp/",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, args) => [
        { type: "product-details", id: args?.glid },
      ],
    }),
    updateCostPerCountry: builder.mutation({
      query: (payload) => ({
        url: "/api/glabp/",
        method: "POST",
        body: payload,
      }),
    }),
    updateAliLink: builder.mutation({
      query: (payload) => ({
        url: "/api/glabp/",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, args) => [
        { type: "product-details", id: args?.glid },
      ],
    }),
  }),
});

export const {
  useGetProductsQuery,
  useGetProductDetailsQuery,
  useGetProductVariantDetailsQuery,
  useGetVariantCostDetailsQuery,
  useGetProductAdStatsQuery,
  useUpdateCostAndSupplyMutation,
  useUpdateAllCostsForProductMutation,
  useUpdateCostPerCountryMutation,
  useUpdateAliLinkMutation,
} = productApi;
